import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { ConversionState, CurrencyService } from '../others/services/currency.service';

/**
 * GlobalCurrencyPipe  ·  {{ amount | localCurrency }}
 *
 * Converts a raw GHS (Ghana Cedi) number from the backend into the visiting
 * user's local currency, using the rate detected from their IP address.
 *
 * Why pure:false?
 *   The conversion rate is held in a service (external mutable state).
 *   Angular only re-evaluates pure pipes when the INPUT argument changes.
 *   Since the rate loads asynchronously AFTER the template first renders,
 *   we must mark the pipe impure so Angular picks up the rate update on the
 *   next change-detection cycle.
 *
 * Performance: the only work done per CD cycle is a single multiply + format.
 */
@Pipe({
    name: 'localCurrency',
    pure: false,          // ← critical: re-evaluates when service state changes
})
export class LocalCurrencyPipe implements PipeTransform, OnDestroy {

    private state: ConversionState;
    private subs: Subscription = new Subscription();
    private angularCurrencyPipe = new CurrencyPipe('en-US');

    constructor(private currencyService: CurrencyService) {
        // Initialise immediately from the current preference
        this.state = currencyService.getCurrentState();

        // Keep in sync with state updates (though logic is now static per session)
        this.subs.add(
            currencyService.conversionState$.subscribe(s => this.state = s)
        );
    }

    transform(
        value: any,
        currencyCodeOverride?: string,
        display: string = 'symbol-narrow',
        digitsInfo: string = '1.2-2'
    ): string | null {
        if (value === null || value === undefined || value === '') return null;

        const amount = Number(value);
        let currencyCode = currencyCodeOverride || this.state.code;

        // Normalize GHC/GH₵ variants to GHS for Angular's CurrencyPipe compatibility
        if (currencyCode === 'GH₵' || currencyCode === 'GHC') {
            currencyCode = 'GHS';
        }

        try {
            return this.angularCurrencyPipe.transform(amount, currencyCode, display, digitsInfo);
        } catch {
            // Fallback for non-Intl symbols or failed formats
            let symbol = this.state.symbol;
            if (currencyCodeOverride) {
                const symbolMap: { [key: string]: string } = { 
                    'USD': '$', 
                    'GHS': 'GH₵', 
                    'GH₵': 'GH₵', 
                    'GHC': 'GH₵', 
                    'EUR': '€', 
                    'GBP': '£', 
                    'NGN': '₦' 
                };
                symbol = symbolMap[currencyCodeOverride.toUpperCase()] || currencyCodeOverride;
            }
            return `${symbol} ${amount.toFixed(2)}`;
        }
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
